import type { RawServingStatusApiResponse } from './ServingStatus';

export interface RawLineItemApiResponse {
  advertised_skus?: string[];
  advertiser_status: LineItemAdvertiserStatus;
  approved_at?: string | null;
  approved_by?: string | null;
  audience_id?: string | null;
  bid_cents?: number | null;
  billing_type?: LineItemBillingType;
  budget_limit?: LineItemBudgetLimit | null;
  budget_total_cents?: number | null;
  budget_total_left_cents?: number | null;
  budget_total_left_percent?: number | null;
  campaign_id: string;
  commitment?: LineItemCommitment | null;
  created_at: string;
  creatives?: { [key: string]: LineItemCreativesValue } | null;
  custom_parameters?: any;
  custom_targeting?: { [key: string]: LineItemsCustomTargetingValue } | null;
  custom?: any;
  delivery_mode?: LineItemDeliveryMode;
  discount_type?: LineItemDiscountType;
  discount_value?: number | null;
  frequency_cap_configs?: LineItemFrequencyCapConfig[];
  geo_targets?: GeoTargeting[] | null;
  goal?: LineItemGoalUpdate | null;
  headline?: string | null;
  id: string;
  keywords?: string[];
  link_url?: string | null;
  name: string;
  pacing_type: LineItemPacingType;
  pending_changes?: LineItemPendingChangesResponse | null;
  placement_id: string;
  retailer_approval: LineItemRetailerApprovalStatus;
  retailer_message?: string | null;
  retailer_status: LineItemRetailerStatus;
  schedule?: LineItemSchedule | null;
  serving?: RawServingStatusApiResponse;
  target_category_keys?: string[];
  tenancy_filter_id?: string | null;
  updated_at: string;
}

export interface GetTargeting {
  lat: number;
  lon: number;
  radius: number;
}

export enum LineItemRetailerStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum LineItemRetailerApprovalStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
}

export enum LineItemAdvertiserStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum LineItemBillingType {
  CPM = 'CPM',
  CLICK = 'CLICK',
  ADD_TO_CART = 'ADD_TO_CART',
}

export enum LineItemPacingType {
  ASAP = 'ASAP',
  EVEN = 'EVEN',
}

export enum LineItemDiscountType {
  PERCENTAGE = 'PERCENTAGE',
  ABSOLUTE = 'ABSOLUTE',
}

export interface LineItemBudgetLimit {
  budget_cents: number;
  budget_interval: LineItemBudgetLimitBudgetInterval;
}

export enum LineItemBudgetLimitBudgetInterval {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum LineItemDeliveryMode {
  AUCTION = 'AUCTION',
  GUARANTEED = 'GUARANTEED',
  HOUSE_AD = 'HOUSE_AD',
}

export const LineItemCommitmentType = {
  VOLUME: 'VOLUME',
  DATES: 'DATES',
} as const;

export type LineItemCommitmentType =
  (typeof LineItemCommitmentType)[keyof typeof LineItemCommitmentType];

// Discriminated union type matching the API structure
export type LineItemCommitment =
  | {
      type: 'VOLUME';
      value: number;
      position?: number | null;
    }
  | {
      type: 'DATES';
      bill_total_at: string;
      position: number;
    };

export interface LineItemCreativesValue {
  url: string;
  duration?: number;
}

export interface LineItemsCustomTargetingValue {
  boolean_expression: string;
}

export interface LineItemSchedule {
  /**
   * List of hours (0-23) representing the active hours for a specific day.
   * Example: [9, 10, 11] represents 9 AM to 12 PM.
   */
  monday: number[];
  /**
   * List of hours (0-23) representing the active hours for a specific day.
   */
  tuesday: number[];
  /**
   * List of hours (0-23) representing the active hours for a specific day.
   */
  wednesday: number[];
  /**
   * List of hours (0-23) representing the active hours for a specific day.
   */
  thursday: number[];
  /**
   * List of hours (0-23) representing the active hours for a specific day.
   */
  friday: number[];
  /**
   * List of hours (0-23) representing the active hours for a specific day.
   */
  saturday: number[];
  /**
   * List of hours (0-23) representing the active hours for a specific day.
   */
  sunday: number[];
}

export interface LineItemPendingChangesResponse {
  advertised_skus?: string[] | null;
  audience_id?: string | null;
  creatives?: any;
  custom_parameters?: any;
  custom_targeting?: any;
  discount_type?: LineItemDiscountType;
  discount_value?: number | null;
  geo_targets?: GeoTargeting[] | null;
  headline?: string | null;
  keywords?: string[] | null;
  link_url?: string | null;
  schedule?: LineItemSchedule | null;
  target_category_keys?: string[] | null;
}

export interface RawCreateLineItemRequestData {
  advertised_skus?: string[];
  audience_id?: string | null;
  bid_cents?: number | null;
  billing_type?: LineItemBillingType;
  budget_limit?: LineItemBudgetLimit | null;
  budget_total_cents?: number | null;
  campaign_id: string;
  commitment?: LineItemCommitment | null;
  creatives?: { [key: string]: LineItemCreativesValue } | null;
  custom_parameters?: any;
  custom_targeting?: { [key: string]: LineItemsCustomTargetingValue } | null;
  custom?: any;
  delivery_mode?: LineItemDeliveryMode;
  discount_type?: LineItemDiscountType;
  discount_value?: number | null;
  frequency_cap_configs?: LineItemFrequencyCapConfig[];
  geo_targets?: GeoTargeting[] | null;
  goal?: LineItemGoalUpdate | null;
  headline?: string | null;
  keywords?: string[];
  link_url?: string | null;
  name: string;
  pacing_type?: LineItemPacingType;
  placement_id: string;
  schedule?: LineItemSchedule | null;
  target_category_keys?: string[];
  tenancy_filter_id?: string | null;
}

export enum LineItemCreatePacingType {
  ASAP = 'ASAP',
  EVEN = 'EVEN',
}

export enum LineItemCreateDiscountType {
  PERCENTAGE = 'PERCENTAGE',
  ABSOLUTE = 'ABSOLUTE',
}

export interface LineItemGoalUpdate {
  type: LineItemGoalType;
  target?: number | null;
  min_bid_cents: number;
}

export enum LineItemGoalType {
  CLICKS = 'clicks',
  ROAS = 'roas',
  CONVERSION = 'conversion',
}

export interface GeoTargeting {
  lat: number;
  lon: number;
  radius: number;
}

export interface RawUpdateLineItemRequestData {
  advertised_skus: string[] | null;
  audience_id: string | null;
  bid_cents: number | null;
  billing_type: LineItemUpdateBillingType;
  budget_limit: LineItemBudgetLimit | null;
  budget_total_cents: number | null;
  commitment?: LineItemCommitment | null;
  creatives: any;
  custom_parameters: any;
  custom_targeting: any;
  custom?: any;
  delivery_mode?: LineItemDeliveryMode;
  discount_type: LineItemUpdateDiscountType;
  discount_value: number | null;
  frequency_cap_configs: LineItemFrequencyCapConfig[];
  geo_targets: GeoTargeting[] | null;
  goal: LineItemGoalUpdate | null;
  headline: string | null;
  keywords: string[];
  link_url: string | null;
  name: string;
  pacing_type: LineItemUpdatePacingType;
  schedule: LineItemSchedule | null;
  target_category_keys: string[] | null;
  tenancy_filter_id: string | null;
}

export interface RawPatchLineItemRequestData {
  advertised_skus?: string[];
  advertiser_status?: LineItemPatchAdvertiserStatus;
  audience_id?: string | null;
  bid_cents?: number | null;
  billing_type?: LineItemUpdateBillingType;
  budget_limit?: LineItemBudgetLimit | null;
  budget_total_cents?: number | null;
  commitment?: LineItemCommitment | null;
  creatives?: { [key: string]: LineItemCreativesValue } | null;
  custom_parameters?: any;
  custom_targeting?: { [key: string]: LineItemsCustomTargetingValue } | null;
  custom?: any;
  delivery_mode?: LineItemDeliveryMode;
  discount_type?: LineItemUpdateDiscountType;
  discount_value?: number | null;
  frequency_cap_configs?: LineItemFrequencyCapConfig[] | null;
  geo_targets?: GeoTargeting[] | null;
  goal?: LineItemGoalUpdate | null;
  headline?: string | null;
  keywords?: string[];
  link_url?: string | null;
  name?: string | null;
  pacing_type?: LineItemUpdatePacingType;
  schedule?: LineItemSchedule | null;
  target_category_keys?: string[];
  tenancy_filter_id?: string | null;
}

export enum LineItemUpdateBillingType {
  CPM = 'CPM',
  CLICK = 'CLICK',
  ADD_TO_CART = 'ADD_TO_CART',
}

export enum LineItemUpdatePacingType {
  ASAP = 'ASAP',
  EVEN = 'EVEN',
}

export enum LineItemUpdateDiscountType {
  PERCENTAGE = 'PERCENTAGE',
  ABSOLUTE = 'ABSOLUTE',
}

export interface LineItemUpdateCreatives {
  urls?: { [key: string]: string };
}

export enum LineItemPatchAdvertiserStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum LineItemStatusPatchRetailerStatus {
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum LineItemRetailerStatusPatchRetailerApproval {
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
}

export interface LineItemFrequencyCapConfig {
  period_unit: LineItemFrequencyCapConfigPeriodUnit;
  period_count: number;
  max_impressions: number;
}

export enum LineItemFrequencyCapConfigPeriodUnit {
  DAY = 'DAY',
}

export function hasSomeCreatives(lineItem: RawLineItemApiResponse): boolean {
  return Boolean(lineItem.creatives) && hasSomeCreativeUrls(lineItem.creatives);
}

export function hasSomeCreativeUrls(
  creatives: { [key: string]: LineItemCreativesValue } | null | undefined,
): boolean {
  return (
    creatives != null &&
    Object.keys(creatives).length > 0 &&
    Object.values(creatives).some((creative) => !!creative.url)
  );
}

export function extractUrls(
  creatives: { [key: string]: LineItemCreativesValue } | null | undefined,
): string[] {
  if (!creatives) {
    return [];
  }

  return Object.values(creatives)
    .map((creative) => creative.url)
    .filter((url): url is string => !!url);
}
